import React, { useEffect, useState } from "react";
import { Grid, FormControl, Select, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";

// react datepicker
import DatePicker from "react-datepicker";
import { ko } from "date-fns/esm/locale";
import "react-datepicker/dist/react-datepicker.css";

import { useFormContext, Controller } from "react-hook-form";

import {
  colorOrdered,
  colorCompleted,
  statusTotal,
  colorInProgress,
  ControlLabel,
  ButtonSearch,
  CustomDateInput,
} from "../index";
import {
  makeStartDate,
  makeEndDate,
  getDateFormat,
  getStatusList,
  startDateBefore,
  startDateAfter,
} from "../../lib";

const summaryCountFontSize = 15;
const summaryCountFontWeight = "bold";

const useStyles = makeStyles(() => ({
  filterContainer: {
    marginLeft: 2,
  },
  summaryContainer: {
    marginRight: 0,
  },

  label: {
    marginTop: 4,
  },
  select: {
    width: 68,
    marginLeft: 12,
  },
  datePicker: {
    marginLeft: 7,
  },
  dateDelimiter: {
    marginLeft: 10,
    marginRight: 10,
  },

  summaryListFirst: {
    width: 80,
    marginTop: 0,
    marginBottom: 0,
    listStyle: "none",
    textAlign: "center",
    padding: 0,
  },
  summaryList: {
    width: 80,
    marginTop: 0,
    marginBottom: 0,
    listStyle: "none",
    textAlign: "center",
    padding: 0,
    borderLeft: "solid 1px #edf0f5",
  },
  summaryOrder: {
    color: colorOrdered,
    fontSize: summaryCountFontSize,
    fontWeight: summaryCountFontWeight,
  },
  summaryInProgress: {
    color: colorInProgress,
    fontSize: summaryCountFontSize,
    fontWeight: summaryCountFontWeight,
  },
  summaryCompleted: {
    color: colorCompleted,
    fontSize: summaryCountFontSize,
    fontWeight: summaryCountFontWeight,
  },
}));

const monthAgo = (month: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - month);

  return d;
};

const SelectStatus = () => {
  const classes = useStyles();
  const [status, setStatus] = useState<number>(statusTotal);

  const { control, setValue } = useFormContext();

  return (
    <Grid container direction={"row"}>
      <Grid item className={classes.label}>
        <ControlLabel name={"상태"} />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={"status"}
          defaultValue={status}
          render={({ field }) => (
            <FormControl className={classes.select}>
              <Select
                {...field}
                defaultValue={status}
                variant={"standard"}
                onChange={(e: any) => {
                  field.onChange(e);
                  setValue("status", e.target.value);
                  setStatus(e.target.value);
                }}
              >
                <MenuItem value={statusTotal}>전체</MenuItem>
                {getStatusList().map(
                  (v: any, i: number) =>
                    v.id !== 3 && (
                      <MenuItem key={i} value={v.id}>
                        {v.status}
                      </MenuItem>
                    )
                )}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
    </Grid>
  );
};

const SelectDate = () => {
  const classes = useStyles();
  const { control } = useFormContext();
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  const dateFormat = getDateFormat();
  const CustomInput = CustomDateInput();
  const maxInquiryDate = new Date().setDate(new Date().getDate() + 7);

  useEffect(() => {
    makeStartDate(startDate);
    startDate.setDate(startDate.getDate() - startDateBefore);
    makeEndDate(endDate);
    endDate.setDate(endDate.getDate() + startDateAfter);
  }, []);

  return (
    <Grid container direction={"row"}>
      <Grid item className={classes.label}>
        <ControlLabel label={"기간"} />
      </Grid>
      <Grid item>
        <Grid container direction={"row"} spacing={1}>
          <Grid item className={classes.datePicker}>
            <Controller
              control={control}
              name={"startDate"}
              defaultValue={startDate}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  locale={ko} // 언어설정 기본값은 영어
                  dateFormat={dateFormat} // 날짜 형식 설정
                  minDate={monthAgo(6)} // 선택할 수 있는 최소 날짜값 지정
                  maxDate={endDate} // 선택할 수 있는 최대 날짜값 지정
                  selected={field.value}
                  onChange={(date: Date) => {
                    const d = makeStartDate(date);
                    field.onChange(d);
                    setStartDate(d);
                  }}
                  customInput={<CustomInput />}
                />
              )}
            />
          </Grid>
          <Grid item className={classes.dateDelimiter}>
            ~
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name={"endDate"}
              defaultValue={endDate}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  locale={ko} // 언어설정 기본값은 영어
                  dateFormat={dateFormat} // 날짜 형식 설정
                  minDate={startDate} // 선택할 수 있는 최소 날짜값 지정
                  maxDate={new Date(maxInquiryDate)} // 선택할 수 있는 최대 날짜값 지정
                  selected={field.value}
                  onChange={(date: Date) => {
                    const d = makeEndDate(date);
                    field.onChange(d);
                    setEndDate(d);
                  }}
                  customInput={<CustomInput />}
                />
              )}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const Summary = (props: any) => {
  const classes = useStyles();
  const { summary } = props;

  return (
    <Grid container direction={"row"}>
      <Grid item>
        <ul className={classes.summaryListFirst}>
          <li>
            <ControlLabel name={"주문"} />
          </li>
          <li className={classes.summaryOrder}>{summary && summary.ordered}</li>
        </ul>
      </Grid>
      <Grid item>
        <ul className={classes.summaryList}>
          <li>
            <ControlLabel name={"배차중"} />
          </li>
          <li className={classes.summaryInProgress}>
            {summary && summary.inProgress}
          </li>
        </ul>
      </Grid>
      <Grid item>
        <ul className={classes.summaryList}>
          <li>
            <ControlLabel name={"배차완료"} />
          </li>
          <li className={classes.summaryCompleted}>
            {summary && summary.completed}
          </li>
        </ul>
      </Grid>
    </Grid>
  );
};

export default function Header(props: any) {
  const classes = useStyles();
  const { summary } = props;

  return (
    <Grid
      container
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Grid item className={classes.filterContainer}>
        <Grid
          container
          direction={"row"}
          justifyContent={"flex-start"}
          spacing={4}
        >
          <Grid item>
            <SelectStatus />
          </Grid>
          <Grid item>
            <SelectDate />
          </Grid>
          <Grid item>
            <ButtonSearch>검색</ButtonSearch>
          </Grid>
        </Grid>
      </Grid>
      <Grid item className={classes.summaryContainer}>
        <Summary summary={summary} />
      </Grid>
    </Grid>
  );
}
